import { Box, Card, CardContent, CardHeader, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { LineChart } from '@mui/x-charts/LineChart';
import { tr, uiLocale } from '../lib/i18n';
import type { ReactNode } from 'react';
import { fmtNum } from '../lib/labels';

/** Google's semantic tones — meaning first, never a decorative rainbow. */
export const TONE = {
  blue: '#0B57D0',
  sky: '#4C8DF6',
  wait: '#E37400',
  good: '#188038',
  bad: '#D93025',
  violet: '#8430CE',
  teal: '#007B83',
  mute: '#9AA0A6',
  ink: '#3C4043',
} as const;

export const PROPERTY_BAR: Record<string, string> = {
  PENDING_REVIEW: TONE.wait,
  AVAILABLE: TONE.good,
  RESERVED: TONE.sky,
  SOLD: TONE.blue,
  RENTED: TONE.teal,
  WITHDRAWN: TONE.bad,
  ARCHIVED: TONE.mute,
};

export const REQUEST_BAR: Record<string, string> = {
  NEW: TONE.wait,
  ASSIGNED: TONE.sky,
  IN_PROGRESS: TONE.blue,
  CONTACTED: TONE.violet,
  MATCHED: TONE.teal,
  WON: TONE.good,
  LOST: TONE.bad,
  CANCELLED: TONE.mute,
};

const axisStyle = (color: string) => ({
  [`& .MuiChartsAxis-line`]: { display: 'none' },
  [`& .MuiChartsAxis-tick`]: { display: 'none' },
  [`& .MuiChartsAxis-tickLabel`]: { fill: color },
  [`& .MuiChartsGrid-line`]: { stroke: 'var(--mui-palette-divider)', strokeDasharray: '0' },
});

export function ChartCard({ title, subtitle, action, children }: { title: string; subtitle?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={title}
        subheader={subtitle}
        action={action}
        subheaderTypographyProps={{ variant: 'caption' }}
      />
      <CardContent sx={{ pt: 0, flex: 1 }}>{children}</CardContent>
    </Card>
  );
}

const NoData = () => (
  <Stack alignItems="center" justifyContent="center" sx={{ py: 6, color: 'text.secondary' }}>
    <Typography variant="body2">{tr('لا توجد بيانات في هذه الفترة', 'No data for this period')}</Typography>
  </Stack>
);

export type BarRow = { label: string; value: number; color?: string };

/** Ranked rows with proportional bars (HTML, so labels never clip in RTL). */
export function BarsCard({ title, rows }: { title: string; rows: BarRow[] }) {
  const m3 = useTheme().palette.m3;
  const data = [...rows].sort((a, b) => b.value - a.value).slice(0, 10);
  const total = data.reduce((s, d) => s + d.value, 0);
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <ChartCard title={title} subtitle={total ? `${tr('المجموع', 'Total')} ${fmtNum(total)}` : undefined}>
      {data.length === 0 ? <NoData /> : (
        <Stack spacing={1.75} sx={{ pt: 0.5 }}>
          {data.map((d) => (
            <Box key={d.label}>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{d.label}</Typography>
                <Typography variant="body2" fontWeight={500}>{fmtNum(d.value)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: 'end' }}>
                  {total ? `${Math.round((d.value / total) * 100)}%` : ''}
                </Typography>
              </Stack>
              <Box sx={{ height: 8, borderRadius: 999, bgcolor: m3.surfaceContainerHigh, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${Math.max(2, (d.value / max) * 100)}%`, borderRadius: 999, bgcolor: d.color ?? m3.primary, transition: 'width 400ms cubic-bezier(.2,0,0,1)' }} />
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </ChartCard>
  );
}

/** Area line for one daily series (Google Analytics look). */
export function TrendChart({ days, data, label, color, height = 260 }: { days: string[]; data: number[]; label: string; color?: string; height?: number }) {
  const theme = useTheme();
  const m3 = theme.palette.m3;
  const c = color ?? m3.primary;
  if (days.length === 0) return <NoData />;
  return (
    <LineChart
      height={height}
      xAxis={[{
        scaleType: 'point',
        data: days,
        valueFormatter: (d: string) => new Date(d).toLocaleDateString(uiLocale(), { day: 'numeric', month: 'short' }),
        tickLabelStyle: { fontSize: 11 },
      }]}
      yAxis={[{ tickMinStep: 1, tickLabelStyle: { fontSize: 11 } }]}
      series={[{ label, data, color: c, curve: 'monotoneX', area: true, showMark: days.length <= 14 }]}
      grid={{ horizontal: true }}
      margin={{ left: 12, right: 44, top: 16, bottom: 28 }}
      slotProps={{ legend: { hidden: true } }}
      sx={{
        ...axisStyle(m3.onSurfaceVariant),
        '& .MuiAreaElement-root': { fillOpacity: 0.1 },
        '& .MuiLineElement-root': { strokeWidth: 2.5 },
      }}
    />
  );
}

/** Daily trend card (kept for other pages). */
export function TrendCard({ title, days, series }: { title: string; days: string[]; series: { label: string; data: number[] }[] }) {
  return (
    <ChartCard title={title}>
      <TrendChart days={days} data={series[0]?.data ?? []} label={series[0]?.label ?? ''} />
    </ChartCard>
  );
}

/** Ring gauge with a big number in the middle. */
export function RingGauge({ value, size = 132, color }: { value: number; size?: number; color?: string }) {
  const m3 = useTheme().palette.m3;
  return (
    <Box sx={{ width: size, height: size }}>
      <Gauge
        value={value}
        valueMin={0}
        valueMax={100}
        innerRadius="78%"
        cornerRadius="50%"
        text={({ value: v }) => `${fmtNum(v ?? 0)}%`}
        sx={{
          [`& .${gaugeClasses.valueText}`]: { fontSize: size * 0.2, fontWeight: 400 },
          [`& .${gaugeClasses.valueText} text`]: { fill: m3.onSurface },
          [`& .${gaugeClasses.valueArc}`]: { fill: color ?? m3.primary },
          [`& .${gaugeClasses.referenceArc}`]: { fill: m3.surfaceContainerHighest },
        }}
      />
    </Box>
  );
}

/** Fill every day in [from, to] so the line does not skip empty days. */
export function dayRange(from: string, to: string): string[] {
  const out: string[] = [];
  const d = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (d <= end && out.length < 400) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}
